import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { searchAdminPostings, transferPostingOwner, extractApiErrorMessage } from '../../../api/adminApi';
import TransferOwnerModal from './TransferOwnerModal';
import { postingStatusMeta } from '../../../shared/castings/postingStatus';

const STATUS_FILTERS = [
  { key: '',         label: '전체' },
  { key: 'active',   label: '모집중' },
  { key: 'closed',   label: '마감' },
  { key: 'draft',    label: '임시저장' },
];

const AVATAR_COLORS = ['#7F77DD','#D4537E','#1D9E75','#BA7517','#185FA5','#993556'];
const getColor = id => AVATAR_COLORS[Number(id) % AVATAR_COLORS.length];

export default function AdminPostingsPage() {
  const navigate = useNavigate();
  const [query,    setQuery]    = useState('');
  const [status,   setStatus]   = useState('');
  const [postings, setPostings] = useState([]);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [transfer, setTransfer] = useState(null);   // 소유자 변경 대상 공고
  // 이관 직후 안내. 서버에 이력이 남지 않으므로 이전 소유자를 여기에 들고 있다가
  // 되돌리기를 제공한다. 새로고침하면 사라지므로 '방금 실수'만 커버한다.
  const [notice,   setNotice]   = useState(null);
  const [undoing,  setUndoing]  = useState(false);

  const LIMIT = 20;

  // 늦게 도착한 예전 응답이 최신 결과를 덮어쓰지 않도록 요청마다 번호를 매긴다
  const reqIdRef = useRef(0);
  // 검색 버튼으로 '확정된' 검색어 — 입력만 하고 안 누른 글자가 조회에 섞이지 않게
  const searchTermRef = useRef('');

  const fetchPostings = useCallback(async (q, st, p) => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await searchAdminPostings({ q, status: st, page: p, limit: LIMIT });
      if (myReq !== reqIdRef.current) return;
      setPostings(data.postings);
      setTotal(data.total);
    } catch (err) {
      if (myReq !== reqIdRef.current) return;
      // 상태 코드만으로는 원인을 알 수 없다. 서버가 사유를 보냈으면 그대로 보여준다.
      const code = err.response?.status;
      const detail = extractApiErrorMessage(err);
      setError(
        detail
          ? `공고 목록을 불러오지 못했습니다 — ${detail}${code ? ` (HTTP ${code})` : ''}`
          : code
            ? `공고 목록을 불러오지 못했습니다. (HTTP ${code})`
            : (err.message || '공고 목록을 불러오지 못했습니다.')
      );
      setPostings([]);
      setTotal(0);
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPostings(searchTermRef.current, status, page);
  }, [page, status, fetchPostings]);

  const handleSearch = () => {
    searchTermRef.current = query.trim();
    if (page !== 1) { setPage(1); return; }   // useEffect가 조회 — 중복 요청 방지
    fetchPostings(query.trim(), status, 1);
  };

  /** 방금 옮긴 공고를 이전 소유자에게 되돌린다 (서버 이력이 없어 이 세션 안에서만 가능) */
  const handleUndo = async () => {
    if (!notice?.previousOwnerId || undoing) return;
    setUndoing(true);
    try {
      await transferPostingOwner(notice.postingId, notice.previousOwnerId);
      setNotice({ text: `${notice.previousOwnerName} 소유로 되돌렸습니다.`, previousOwnerId: null });
      fetchPostings(searchTermRef.current, status, page);
    } catch (err) {
      setError(`되돌리기에 실패했습니다. (HTTP ${err.response?.status ?? '네트워크'})`);
    } finally {
      setUndoing(false);
    }
  };

  const handleStatusFilter = (st) => {
    setStatus(st);
    setPage(1);
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <AdminLayout pageId="postings">
      <div className="admin-topbar">
        <h1>공고 관리</h1>
        <div className="admin-topbar__right">
          <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
            총 {total.toLocaleString()}개 공고
          </span>
          <span className="admin-badge"><i className="ti ti-user" /> admin</span>
        </div>
      </div>

      {notice && (
        <div className="ag-card" style={{ marginBottom: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <i className="ti ti-circle-check" style={{ color: 'var(--color-success, #1D9E75)' }} />
          <span>{notice.text}</span>
          <div style={{ flex: 1 }} />
          {notice.previousOwnerId != null && (
            <button className="ag-btn ag-btn--sm" disabled={undoing} onClick={handleUndo}>
              <i className="ti ti-arrow-back-up" /> {undoing ? '되돌리는 중...' : `${notice.previousOwnerName} 소유로 되돌리기`}
            </button>
          )}
          <button className="ag-btn ag-btn--sm" onClick={() => setNotice(null)}>닫기</button>
        </div>
      )}

      {/* 검색 바 */}
      <div className="ag-card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="ag-field__input"
            type="text"
            placeholder="공고 제목 또는 제작사명"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            style={{ flex: 1 }}
          />
          <button className="ag-btn ag-btn--primary" style={{ flexShrink: 0 }} onClick={handleSearch}>
            <i className="ti ti-search" /> 검색
          </button>
        </div>
      </div>

      {/* 상태 필터 */}
      <div className="ag-filter-row">
        <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)', flexShrink: 0 }}>상태:</span>
        {STATUS_FILTERS.map(f => (
          <button
            key={f.key}
            className={`ag-filter-chip${status === f.key ? ' active' : ''}`}
            onClick={() => handleStatusFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ fontSize: 12, color: 'var(--color-danger)', marginBottom: 10 }}>
          <i className="ti ti-alert-triangle" /> {error}
        </div>
      )}

      <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 10 }}>
        {loading ? '불러오는 중...' : `${postings.length.toLocaleString()}개 공고`}
      </div>

      {/* 테이블 */}
      <div className="ag-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="ag-table">
          <thead>
            <tr>
              {['공고 제목', '제작사', '상태', '지원자', '마감일', '등록일', '상세'].map(h => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="ag-table__empty">
                  <i className="ti ti-loader" style={{ fontSize: 20, display: 'block', marginBottom: 8 }} />
                  불러오는 중...
                </td>
              </tr>
            ) : postings.length === 0 ? (
              <tr>
                <td colSpan={7} className="ag-table__empty">
                  <i className="ti ti-clipboard-x" style={{ fontSize: 20, display: 'block', marginBottom: 8 }} />
                  공고가 없습니다
                </td>
              </tr>
            ) : postings.map(p => {
              const s = postingStatusMeta(p.status);
              const color = getColor(p.id);
              return (
                <tr key={p.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div
                        className="ag-avatar"
                        style={{ background: color + '22', color, fontSize: 11, fontWeight: 700, flexShrink: 0 }}
                      >
                        {(p.title ?? '?').slice(0, 2)}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{p.title}</span>
                    </div>
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>{p.companyName ?? '—'}</span>
                      <button
                        className="ag-btn ag-btn--sm"
                        title="소유 제작사 변경"
                        aria-label={`${p.title} 소유자 변경`}
                        onClick={() => { setNotice(null); setTransfer(p); }}
                      >
                        <i className="ti ti-exchange" /> 변경
                      </button>
                    </div>
                  </td>
                  <td><span className={`ag-badge ${s.cls}`}>{s.label}</span></td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <i className="ti ti-users" style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }} />
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{p.applicationCount ?? 0}</span>
                      <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>명</span>
                    </div>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    {p.deadline?.slice(0, 10) ?? '—'}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                    {p.createdAt?.slice(0, 10) ?? '—'}
                  </td>
                  <td>
                    <button className="ag-btn ag-btn--sm" onClick={() => navigate(`/admin/postings/${p.id}`)}>
                      <i className="ti ti-eye" /> 보기
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="ag-pagination">
          <button className="ag-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
            <i className="ti ti-chevron-left" />
          </button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page + i - 3;
            if (p < 1 || p > totalPages) return null;
            return (
              <button
                key={p}
                className={`ag-page-btn${page === p ? ' active' : ''}`}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            );
          })}
          <button className="ag-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
            <i className="ti ti-chevron-right" />
          </button>
        </div>
      )}
      {transfer && (
        <TransferOwnerModal
          posting={transfer}
          onClose={() => setTransfer(null)}
          onDone={(account) => {
            const to = account.companyName ?? `제작사 #${account.producerProfileId}`;
            setNotice({
              text: `'${transfer.title}' 공고를 ${to} 소유로 옮겼습니다.`,
              postingId: transfer.id,
              previousOwnerId: transfer.companyId ?? transfer.producerProfileId ?? null,
              previousOwnerName: transfer.companyName ?? '이전 제작사',
            });
            setTransfer(null);
            fetchPostings(searchTermRef.current, status, page);  // 제작사 칸을 새 소유자로 갱신
          }}
        />
      )}
    </AdminLayout>
  );
}
